import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ShieldCheck, BadgeCheck, HandHeart, Sparkles, Compass, Target, Flag } from 'lucide-react';
import Navbar from '../../components/public/Navbar';
import Footer from '../../components/public/Footer';
import config from '../../config';

// Every string below is verbatim from the BookMyJab Brand Identity deck.

const PILLARS = [
  { title: 'Qualified Professionals', desc: 'Every visit delivered by qualified healthcare professionals.' },
  { title: 'Clinical Reliability', desc: 'Reliable processes and well-defined SOPs on every visit.' },
  { title: 'Intelligent Technology', desc: 'Technology that simplifies vaccination without removing the human touch.' },
  { title: 'Trusted Experience', desc: 'Trust earned through every interaction — from booking to follow-up.' },
];

const VALUES = [
  { title: 'Clinical Integrity', desc: 'We do what is medically and ethically right.' },
  { title: 'Trust Before Transaction', desc: 'Long-term confidence matters more than short-term revenue.' },
  { title: 'Professional Excellence', desc: 'Preparation, punctuality, communication and execution reflect high standards.' },
  { title: 'Human Dignity', desc: 'Every patient is treated with patience, empathy and respect.' },
  { title: 'Thoughtful Innovation', desc: 'Technology simplifies vaccination without removing the human touch.' },
];

const PROMISE = [
  { title: 'Confidence', desc: 'Clear communication, every step of the way.', icon: ShieldCheck },
  { title: 'Professionalism', desc: 'Trained experts and disciplined execution.', icon: BadgeCheck },
  { title: 'Care', desc: 'Genuine warmth for every family we visit.', icon: HandHeart },
];

const ASSURED = [
  'Qualified healthcare professionals',
  'Authentic vaccines sourced through approved channels',
  'Adherence to cold-chain and clinical protocols',
  'Transparent communication and pricing',
  'Accurate digital vaccination records',
  'Respectful, punctual and hygienic service',
  'Thoughtful post-vaccination support',
  'Protection of privacy and personal data',
];

const SectionEyebrow = ({ children }) => (
  <span
    className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#f37721]"
    style={{ fontFamily: 'Montserrat, sans-serif' }}
  >
    {children}
  </span>
);

export default function About() {
  return (
    <div className="min-h-screen bg-[#fbf8f2]">
      <Navbar />
      <main data-testid="about-page">
        {/* Hero */}
        <section className="pt-24 pb-16 sm:pt-28 sm:pb-20 bg-white border-b border-[#673c96]/10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <div
              data-testid="about-tagline-signature"
              className="text-[11px] italic font-medium text-[#311b52]/70 tracking-[0.16em] uppercase mb-4"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Uncompromising Care. Seamlessly Yours.
            </div>
            <h1
              className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#311b52] leading-tight"
              style={{ fontFamily: 'Poppins, sans-serif' }}
              data-testid="about-hero-title"
            >
              A refined and professionally managed home clinical wellness experience.
            </h1>
            <p
              className="mt-5 text-[#3e195e]/80 text-sm sm:text-base leading-relaxed max-w-2xl mx-auto"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              BookMyJab is a technology-enabled home clinical healthcare service provider, delivering uncompromising clinical care, private health management, and flawlessly executed wellness experiences directly to the comfort of home.
            </p>
            <p
              className="mt-5 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#f37721]"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Premium in experience. Sensible in access.
            </p>
          </div>
        </section>

        {/* Purpose · Vision · Mission */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14 md:py-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { icon: Compass, eyebrow: 'Purpose', body: 'To command absolute certainty in advanced wellness and protection. We ensure that every individual and family experiences the finest of clinical excellence — defined by profound respect, uncompromised safety, and an effortlessly orchestrated care experience within the privacy of their home.' },
              { icon: Target, eyebrow: 'Vision', body: 'To set the benchmark for trusted clinical wellness and specialized home care through exceptional medical standards, dependable service, and thoughtfully designed customer experiences.' },
              { icon: Flag, eyebrow: 'Mission', body: 'To deliver safe, professionally managed and technology-enabled home clinical wellness services through qualified healthcare professionals, reliable processes, well-defined SOPs, and an uncompromising commitment to trust.' },
            ].map((b, i) => (
              <motion.div
                key={b.eyebrow}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                data-testid={`about-block-${b.eyebrow.toLowerCase()}`}
                className="rounded-3xl bg-white border border-[#673c96]/10 p-6 sm:p-8"
              >
                <div className="w-10 h-10 rounded-xl bg-[#311b52]/8 flex items-center justify-center">
                  <b.icon className="w-5 h-5 text-[#311b52]" />
                </div>
                <div className="mt-4 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#f37721]" style={{ fontFamily: 'Montserrat, sans-serif' }}>{b.eyebrow}</div>
                <p className="mt-2 text-[14px] text-[#311b52]/90 leading-relaxed" style={{ fontFamily: 'Montserrat, sans-serif' }}>{b.body}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* 4 Pillars */}
        <section className="bg-white border-y border-[#673c96]/10 py-14 md:py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10">
              <SectionEyebrow>The four pillars</SectionEyebrow>
              <h2 className="mt-3 text-2xl sm:text-3xl font-bold text-[#311b52]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Built on qualified people, reliable processes, and thoughtful technology.
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {PILLARS.map((p, i) => (
                <div key={p.title} data-testid={`about-pillar-${i}`} className="rounded-2xl bg-[#fbf8f2] border border-[#673c96]/10 p-5">
                  <div className="text-sm font-bold text-[#311b52]" style={{ fontFamily: 'Poppins, sans-serif' }}>{p.title}</div>
                  <div className="mt-1.5 text-[13px] text-[#3e195e]/80 leading-relaxed" style={{ fontFamily: 'Montserrat, sans-serif' }}>{p.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Brand Promise */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14 md:py-20">
          <div className="text-center mb-10">
            <SectionEyebrow>Our brand promise</SectionEyebrow>
            <h2 className="mt-3 text-2xl sm:text-3xl font-bold text-[#311b52]" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Every experience delivered with absolute certainty,<br className="hidden sm:block" /> flawless professionalism, and profound care.
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {PROMISE.map((p) => (
              <div key={p.title} className="rounded-2xl bg-white border border-[#673c96]/10 p-6 flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#f37721]/10 flex items-center justify-center shrink-0">
                  <p.icon className="w-5 h-5 text-[#f37721]" />
                </div>
                <div>
                  <div className="text-sm font-bold text-[#311b52]" style={{ fontFamily: 'Poppins, sans-serif' }}>{p.title}</div>
                  <div className="mt-1.5 text-[13px] text-[#3e195e]/80 leading-relaxed" style={{ fontFamily: 'Montserrat, sans-serif' }}>{p.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Core Values */}
        <section className="bg-white border-y border-[#673c96]/10 py-14 md:py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10">
              <SectionEyebrow>Our core values</SectionEyebrow>
              <h2 className="mt-3 text-2xl sm:text-3xl font-bold text-[#311b52]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                The principles that guide every decision.
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {VALUES.map((v, i) => (
                <div key={v.title} data-testid={`about-value-${i}`} className="rounded-2xl bg-[#fbf8f2] border border-[#673c96]/10 p-5">
                  <div className="text-sm font-bold text-[#311b52]" style={{ fontFamily: 'Poppins, sans-serif' }}>{v.title}</div>
                  <div className="mt-1.5 text-[13px] text-[#3e195e]/80 leading-relaxed" style={{ fontFamily: 'Montserrat, sans-serif' }}>{v.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Customer Promise — ASSURED */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14 md:py-20">
          <div className="text-center mb-10">
            <SectionEyebrow>Our customer promise</SectionEyebrow>
            <h2 className="mt-3 text-2xl sm:text-3xl font-bold text-[#311b52]" style={{ fontFamily: 'Poppins, sans-serif' }}>Assured — our service charter to every family.</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {ASSURED.map((a, i) => (
              <div key={a} data-testid={`about-assured-${i}`} className="flex items-start gap-3 rounded-xl bg-white border border-[#673c96]/10 px-5 py-4">
                <ShieldCheck className="w-4 h-4 text-[#f37721] mt-0.5 shrink-0" />
                <div className="text-[14px] text-[#311b52]/90" style={{ fontFamily: 'Montserrat, sans-serif' }}>{a}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Manifesto */}
        <section className="bg-[#311b52] text-white py-16 md:py-24">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <SectionEyebrow>The BookMyJab manifesto</SectionEyebrow>
            <p
              className="mt-6 text-lg sm:text-xl leading-relaxed text-white/90"
              style={{ fontFamily: 'Poppins, sans-serif' }}
              data-testid="about-manifesto"
            >
              Every clinical decision represents a choice to protect. A parent safeguarding a child. An adult preparing for travel. A family caring for an aging parent. A person choosing prevention over uncertainty.
              <br /><br />
              These moments deserve more than a routine appointment. They deserve preparation, professionalism, respect and confidence.
              <br /><br />
              At BookMyJab, trust is built through expertise, strengthened by process and remembered through experience. Because true protection begins long before the care arrives.
              <br /><br />
              <span className="italic text-[#f37721]">It begins with trust.</span>
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/vaccination-catalogue" data-testid="about-request-btn"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#f37721] text-white font-semibold hover:brightness-105 transition">
                Book now <Sparkles className="w-4 h-4" />
              </Link>
              <a href={config.getWhatsAppLink()} target="_blank" rel="noopener noreferrer" data-testid="about-whatsapp-btn"
                className="inline-flex items-center justify-center gap-2 px-4 py-2 text-xs font-medium text-white/50 hover:text-white/80 transition">
                Message on WhatsApp
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
